<template>
  <v-container ma-0 pa-0>
    <v-layout justify-center row wrap mb-2>
      <v-flex xs12 sm6 pa-0>
        <h1 class="display-1 mt-2 ml-2">Membership Registry</h1>
        <v-spacer/>
      </v-flex>
      <v-flex xs12 sm6 text-xs-right pt-2>
        <v-btn :loading="tableLoading" title="Clear Filters" outline small @click="clearFilters()">
          clear
        </v-btn>
        <v-btn title="Show Advanced Search Filters" depressed small outline color="" @click="showAdvanced = !showAdvanced">
            MORE
            <v-icon v-if="!showAdvanced">keyboard_arrow_down</v-icon>
            <v-icon v-else>keyboard_arrow_up</v-icon>
          </v-btn>
      </v-flex>
    </v-layout>
    <v-layout row wrap>
      <v-flex xs12 md10 xl11 pa-2>
        <v-text-field
          class="pa-0"
          flat
          v-model="filters.text"
          label="Search by Names, Email or Membership Number"
          clearable
          v-on:keyup.enter="applyFilter()"
          single-line
          hide-details
          background-color="transparent"
          />
      </v-flex>
      <v-flex xs12 md2 xl1 text-xs-right pa-2>
          <v-btn title="Apply Filters" small block depressed dark color="info" @click="applyFilter()">
            search
          </v-btn>
      </v-flex>
    </v-layout>
    <v-layout row wrap v-if="showAdvanced" px-2>
      <v-flex xs12 md6 xl4 mb-2>
        <p class="ma-0 mb-1">Account Status</p>
        <v-btn-toggle v-model="filters.status" title="Account Status">
          <v-btn flat value="all">
            all
          </v-btn>
          <v-btn flat value="active">
            active
          </v-btn>
          <v-btn flat value="inactive">
            inactive
          </v-btn>
          <v-btn flat value="unconfirmed">
            unconfirmed
          </v-btn>
        </v-btn-toggle>
      </v-flex>
      <v-flex xs12 md6 xl4 mb-2>
        <p class="ma-0 mb-1">Account Type</p>
        <v-btn-toggle v-model="filters.type" title="Account Type">
          <v-btn flat value="all">
            all
          </v-btn>
          <v-btn flat value="member">
            member
          </v-btn>
          <v-btn flat value="life">
            life
          </v-btn>
          <v-btn flat value="sponsor">
            sponsor
          </v-btn>
        </v-btn-toggle>
      </v-flex>
      <v-flex xs12 md6 xl4 mb-2>
        <p class="ma-0 mb-1">Membership Level</p>
        <v-btn-toggle v-model="filters.level" title="Membership Level">
          <v-btn flat value="all">
            all
          </v-btn>
          <v-btn flat value="general">
            general
          </v-btn>
          <v-btn flat value="committee">
            committee
          </v-btn>
          <v-btn flat value="executive">
            executive
          </v-btn>
        </v-btn-toggle>
      </v-flex>
      <v-flex xs12 md6 xl2>
        <v-switch
          class="mt-1"
          v-model="filters.created"
          label="New Accounts"
          title="Only show members made in the last 30 days"
        ></v-switch>
      </v-flex>
      <v-flex xs12 md6 xl2>
        <v-switch
          class="mt-1"
          v-model="filters.renewal"
          label="Renewals Due"
          title="Only show members who have renewal payments due"
        ></v-switch>
      </v-flex>
    </v-layout>
    <v-divider/>
    <v-layout row wrap justify-center>
      <v-flex xs12>
        <v-data-table
          :headers="tableHeaders"
          :items="accounts"
          :expand="tableExpand"
          :pagination.sync="tablePagination"
          :total-items="totalAccounts"
          :loading="tableLoading"
          class=""
        >
          <template v-slot:items="props">
            <tr class="tableRow" @click="props.expanded = !props.expanded">
              <td>{{ props.item.memNo ? `${('0000' + props.item.memNo).slice(-4)}` : '-' }}</td>
              <td class="text-xs-left">
                <span class="memberStatus" :title="statusTitle(props.item.status)" :class="props.item.status">{{ props.item.status }}</span>
              </td>
              <td class="text-xs-left">{{ props.item.name }}</td>
              <td class="text-xs-left">{{ props.item.email }}</td>
              <td class="text-xs-left">{{ props.item.type }}</td>
              <td class="text-xs-left">{{ props.item.level }}</td>
              <td class="text-xs-left">{{ formatDate(props.item.created) }}</td>
              <td class="text-xs-left" :class="renewClass(props.item.renewal)">{{ formatDate(props.item.renewal) }}</td>
              <td class="text-xs-left">
                <v-icon v-if="!props.expanded" >keyboard_arrow_down</v-icon>
                <v-icon v-else >keyboard_arrow_up</v-icon>
              </td>
            </tr>
          </template>
          <template v-slot:expand="props">
            <memberExpand :member="props.item.meta" v-on:viewMember="gotoMember($event)" v-on:updateUser="updateMeta($event)" />
          </template>
          <template v-slot:no-data>
            <v-layout row wrap>
              <v-flex xs12 text-xs-center>
                No results match. Try changing your search parameters and try again.
              </v-flex>
            </v-layout>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import memberExpand from '@/components/MembersExpand'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    memberExpand
  },
  data () {
    return {
      firstTime: true,
      showAdvanced: false,
      accounts: [],
      totalAccounts: 0,
      tableHeaders: [{
        text: 'Membership No.',
        sortable: true,
        value: 'memNo'
      },
      { text: 'Status', value: 'status' },
      { text: 'Account Holder Name', value: 'name' },
      { text: 'Email', value: 'email' },
      { text: 'Membership Type', value: 'type' },
      { text: 'Membership Level', value: 'level' },
      { text: 'Created On', value: 'created' },
      { text: 'Next Renewal', value: 'renewal' },
      { text: '', sortable: false }
      ],
      tableExpand: false,
      tableLoading: false,
      tablePagination: {
        sortBy: 'name',
        rowsPerPage: 10,
        page: +this.$route.query.p || 1,
      },
      filters: {
        text: null,
        status: 'all',
        type: 'all',
        level: 'all',
        created: false,
        renewal: false,
        page: 1
      },
      days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
    }
  },
  watch: {
    tablePagination: {
      async handler () {
        console.log('watch pagination')
        const data = await this.getData()
        this.accounts = data.items
        this.totalAccounts = data.total
      }
    }
  },
  computed: {
    ...mapGetters({
      members: 'getMembers',
      memberCount: 'getTotalCount',
      totalFilterCount: 'getFilterCount'
    })
  },
  methods: {
    ...mapActions([
      'getMembers',
      'applyMemberFilters',
      'setTestMembers',
      'setMemberPage',
      'updateMembers'
    ]),
    async reloadTable () {
      // console.log('Reloading Table...')
      // const data = await this.getData(true)
      // this.accounts = data.items
      // this.totalAccounts = data.total
    },
    statusTitle (status = null) {
      switch (status) {
        case 'active':
          return 'Active: A member who has an active registration'
        case 'inactive':
          return 'Inactive: A member who needs to renew their registration'
        case 'unconfirmed':
          return 'Unconfirmed: A member who has not activated their account'
        default:
          return null
      }
    },
    checkExpired (timestamp) {
      let checkDate = new Date(new Date().toDateString())
      let renewal = new Date(new Date(timestamp).toDateString())
      return (renewal < checkDate)
    },
    renewClass (timestamp) {
      let checkDate = new Date(new Date().toDateString())
      let renewal = new Date(new Date(timestamp).toDateString())
      let soonDate = new Date(checkDate.toDateString())
      soonDate.setMonth(soonDate.getMonth() + 1)
      if (renewal < checkDate) return 'orange--text'
      // if (renewal >= checkDate && renewal <= soonDate) return 'orange--text'
      return ''
    },
    async processMembers () {
      let accounts = []
      this.members.map(item => {
        let memNo = null
        if (item.membership && item.membership.membershipNo) {
          memNo = item.membership.membershipNo || null
        }

        let status = item.status
        if (item.status === 'unconfirmed' || !item.membership || !item.membership.current) status = 'unconfirmed'
        else if (this.checkExpired(item.membership.current.subscriptionEnds)) status = 'inactive'
        else status = 'active'

        let type = 'member'
        if (item.membership && item.membership.type) type = item.membership.type

        let level = 'general'
        if (item.committee && item.committee.active) level = 'committee'
        if (item.executive && item.executive.active) level = 'executive'

        let renewal = null
        if (item.membership && item.membership.current) renewal = item.membership.current.subscriptionEnds

        let account = {
          id: item.id,
          created: item.accountCreated,
          status,
          memNo: memNo,
          name: `${item.details.firstName} ${item.details.lastName}`,
          email: item.details.email,
          type,
          level,
          renewal,
          meta: item
        }
        accounts.push(account)
      })

      return accounts
    },
    hasText (text = null, account = null) {
      if (!text || !account) return false
      let found = false
      let expr = new RegExp(`${text}`.trim(), 'i')

      if (expr.test(`${account.name}`)) found = true
      if (expr.test(`${account.email}`)) found = true
      let memNo = (account.memNo) ? `${('0000' + account.memNo).slice(-4)}` : null
      if (memNo && expr.test(memNo)) found = true

      return found
    },
    pastCreated (timestamp) {
      let checkDate = new Date(new Date().toDateString())
      checkDate.setMonth(checkDate.getMonth() - 1)
      let created = new Date(new Date(timestamp).toDateString())
      return (created >= checkDate)
    },
    pastRenew (timestamp) {
      let checkDate = new Date(new Date().toDateString())
      let renewal = new Date(new Date(timestamp).toDateString())
      return (renewal <= checkDate)
    },
    async filterAccounts (accounts = []) {
      if (!accounts) return []
      console.log('Filtering Accounts...')
      const { text, status, type, level, renewal, created } = this.filters

      if (!text && status === 'all' && type === 'all' && level === 'all' && !created && !renewal) {
        console.log('No Filters Set; Return fill Account List')
        return accounts
      }

      let filtered = []
      accounts.map(account => {
        let hasText = false
        let hasStatus = false
        let hasType = false
        let hasLevel = false
        let hasCreated = false
        let hasRenew = false

        let add = false

        // Individial Filters
        if (!text || this.hasText(text, account)) hasText = true
        if (status === 'all' || account.status === status) hasStatus = true
        if (type === 'all' || account.type === type) hasType = true
        if (level === 'all' || account.level === level) hasLevel = true
        if (!created || this.pastCreated(account.created)) hasCreated = true
        if (!renewal || this.pastRenew(account.renewal)) hasRenew = true

        // Check Combined
        if (hasText && hasStatus && hasType && hasLevel && hasCreated && hasRenew) add = true

        // is member in filter?
        if (add) filtered.push(account)
      })

      return filtered
    },
    async getData (reset = false) {
      console.log('Data Table - Get Data...')
      this.tableLoading = true
      if (!this.members || this.firstTime) {
        await this.getMembers()
        this.firstTime = false
      }
      let accounts = await this.processMembers()
      console.log('# Accounts: ', accounts.length)
      // Apply Custom Filters
      accounts = await this.filterAccounts(accounts)
      console.log('# Filtered: ', accounts.length)
      // Apply Pagination, Sorting

      // if (this.$route.query.p && this.$route.query.p !== 1) this.tablePagination.page = this.$route.query.p
      const { sortBy, descending, page, rowsPerPage } = this.tablePagination
      console.log('Sort/Page Params: ', sortBy, descending, page, rowsPerPage)
      if (this.tablePagination.sortBy) {
        accounts = accounts.sort((a, b) => {
          const sortA = a[sortBy]
          const sortB = b[sortBy]

          if (descending) {
            if (sortA < sortB) return 1
            if (sortA > sortB) return -1
            return 0
          } else {
            if (sortA < sortB) return -1
            if (sortA > sortB) return 1
            return 0
          }
        })
      }

      const result = {
        items: [],
        total: accounts.length
      }

      result.items = accounts
      if (rowsPerPage > 0) {
        result.items = accounts.slice((page - 1) * rowsPerPage, page * rowsPerPage)
      }

      console.log('Table Generated!')
      console.log('---===---===---===---')
      this.tableLoading = false
      return result
    },
    gotoMember (id, event) {
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }

      this.$router.push(`/admin/members/${id}`)
    },
    updateMeta (params, event) {
      console.log('Update user Trigger', params)
      let newMembers = []
      this.members.map(item => {
        if (item.id === params.id) {
          console.log('found user:')
          newMembers.push(params.update)
        } else newMembers.push(item)
      })

      let newAcccounts = []
      this.accounts.map(item => {
         if (item.id === params.id) {
          console.log('found user:')
          let account = item
          account.status = 'unconfirmed'
          newAcccounts.push(account)
        } else newAcccounts.push(item)
      })

      this.accounts = newAcccounts
      this.updateMembers({members: newMembers})
      this.getData()
    },
    loadFilters: async function () {
      this.filters.text = this.$route.query.s
      this.filters.page = this.$route.query.p
      this.tablePagination.page = this.$route.query.p
    },
    applyFilter: async function () {
      console.log('Applying Filters...')

      this.$router.push({ path: '/admin/members', query: { s: this.filters.text }})

      const data = await this.getData()
      this.accounts = data.items
      this.totalAccounts = data.total
    },
    clearFilters: async function () {
      this.filters = {
        text: null,
        status: 'all',
        type: 'all',
        level: 'all',
        created: false,
        renewal: false,
        page: 1
      }

      this.$router.replace('/')
      // this.$router.push('/admin/members')
      

      this.applyFilter()
    },
    formatDate: function (timestring, type = null) {
      if (!timestring) return '-'
      let time = new Date(timestring)
      switch (type) {
        case 'full':
          return `${this.days[time.getDay()]}, ${time.getDate()} ${this.months[time.getMonth()]} ${time.getFullYear()}`
        case 'since':
          let newFormat = `${time.getDate()}/${time.getMonth() + 1}/${time.getFullYear()}`
          let nowYear = new Date().getFullYear()
          let yearDiff = nowYear - time.getFullYear()

          if (yearDiff === 0) return newFormat
          if (yearDiff === 1) return newFormat + ' (1 Year)'
          return newFormat + ` (${yearDiff} Years)`
        default:
          return `${time.getDate()}/${time.getMonth() + 1}/${time.getFullYear()}`
      }
    },
    memberStatus: function (member) {
      if (!member.status || member.status === 'unconfirmed') return 'Unconfirmed'
      if (member.status === 'unregistered') return 'Inactive'
      if (this.isMembershipExpired(member)) return 'Renewal Due'
      return 'Active'
    },
    isMembershipExpired: function (member) {
      if (!member.membership.current) return true
      return (new Date(new Date().toDateString()) > new Date(new Date(member.membership.current.subscriptionEnds).toDateString()))
    },
    renewalDue: function (renewalDate) {
      console.log('CHECK RENEWAL DUE TIME: Is now within 2months of renewal')
      let now = new Date()
      console.log(`Now: ${now}`)
      let renewal = new Date(renewalDate)
      console.log(`Renewal Date: ${renewal}`)
      let soon = new Date(renewalDate)
      soon.setMonth(soon.getMonth() - 2)
      console.log(`Renweal -2 Months: ${soon}`)
      if (now.getTime() >= soon.getTime()) return true
      return false
    }
  },
  mounted () {
    this.tablePagination.page = 20
    this.loadFilters()
    this.reloadTable()
    // this.setTestMembers(TestMembers.users)
  }
}
</script>
<style>
  #specialOffer {
    font-size: 2rem;
    background-color: darkgreen;
    padding: 5px 20px;
    border-radius: 3px;
    width: fit-content;
    color: white;
  }
  .memberStatus.unconfirmed {
    color: red;
  }
  .memberStatus.unregistered {
    color: orange;
  }
  .memberStatus.renew {
    color: orange;
  }
  .memberStatus.active {
    color: green;
  }
  .memberStatus.inactive {
    color: orange;
  }
  .detailsCard {
    box-shadow: none !important;
    border: solid 1px grey;
  }
  div.v-expansion-panel__header {
    padding-top: 0;
    padding-bottom: 0;
  }
  .tableRow .viewMoreBtn {
    opacity: 0;
    -webkit-transition: opacity .2s;
    transition: opacity .2s;
    border-radius: 20px;
    padding:2px;
  }

  input#textSearch {
    margin:0;
  }

  .tableRow:hover .viewMoreBtn {
    opacity: 1;
    background-color: rgba(0,0,0,0.2);
  }
</style>
